import {
  chargeEnvelopeSchema,
  chargeListEnvelopeSchema,
  createChargeRequestSchema,
  createPaymentRequestSchema,
  paymentEnvelopeSchema,
  paymentListEnvelopeSchema,
  statementEnvelopeSchema,
  type ChargeResponse,
  type CreateChargeRequest,
  type CreatePaymentRequest,
  type PaymentResponse,
  type StatementResponse,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * RCM's API surface (`BACKLOG.md` R1 charge capture, R6 patient payments/statement), validated
 * against `@emr/shared`'s own request/response schemas — the exact same schemas
 * `server/src/rcm/chargesRoutes.ts`/`paymentsRoutes.ts` validate request bodies with, matching
 * `api/aesthetics.ts`'s own stated reasoning for why this cannot silently drift from what the server
 * actually accepts and returns.
 */

export type {
  ChargeResponse,
  CreateChargeRequest,
  CreatePaymentRequest,
  PaymentResponse,
  StatementResponse,
};

function rcm(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/rcm${suffix}`;
}

export async function fetchCharges(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ChargeResponse[]> {
  const response = await apiRequest(
    rcm(patientId, '/charges'),
    chargeListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.charges;
}

export async function createCharge(
  patientId: string,
  input: CreateChargeRequest,
): Promise<ChargeResponse> {
  createChargeRequestSchema.parse(input);
  const response = await apiRequest(rcm(patientId, '/charges'), chargeEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.charge;
}

export async function fetchStatement(
  patientId: string,
  signal?: AbortSignal,
): Promise<StatementResponse> {
  const response = await apiRequest(
    rcm(patientId, '/statement'),
    statementEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.statement;
}

export async function fetchPayments(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly PaymentResponse[]> {
  const response = await apiRequest(
    rcm(patientId, '/payments'),
    paymentListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.payments;
}

export async function createPayment(
  patientId: string,
  input: CreatePaymentRequest,
): Promise<PaymentResponse> {
  createPaymentRequestSchema.parse(input);
  const response = await apiRequest(rcm(patientId, '/payments'), paymentEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.payment;
}
